/** Compact relative time for a past ISO timestamp against a supplied clock
 *  (deterministic — the status contract's render helpers inject `now`). */
export function relTime(iso: string, now: Date): string {
  const diff = Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 1000));
  if (diff < 60) return `${String(diff)}s ago`;
  if (diff < 3600) return `${String(Math.floor(diff / 60))}m ago`;
  if (diff < 86400) return `${String(Math.floor(diff / 3600))}h ago`;
  return `${String(Math.floor(diff / 86400))}d ago`;
}

/** Compact countdown to a FUTURE ISO timestamp ("in 45s" / "in 12m"). */
export function untilTime(iso: string, now: Date): string {
  const diff = Math.max(0, Math.round((new Date(iso).getTime() - now.getTime()) / 1000));
  if (diff < 60) return `in ${String(diff)}s`;
  if (diff < 3600) return `in ${String(Math.ceil(diff / 60))}m`;
  return `in ${String(Math.ceil(diff / 3600))}h`;
}

export function formatTimeAgo(timestamp: string): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  if (Number.isNaN(diffMs)) return "";
  if (diffMs < 60_000) return "now";
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 0) return "";
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString([], { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function formatEmailDate(timestamp: string): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" })
    + " \u00B7 "
    + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function formatMessageTime(timestamp: string): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function formatDateSeparator(date: Date): { label: string; isToday: boolean } {
  const now = new Date();
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(date) - startOfDay(now)) / (1000 * 60 * 60 * 24));

  const weekday = date.toLocaleDateString(undefined, { weekday: "short" });

  if (diffDays >= -1 && diffDays <= 1) {
    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
    const relative = rtf.formatToParts(diffDays, "day")
      .filter((p) => p.type === "literal")
      .map((p) => p.value)
      .join("")
      .trim();
    return { label: `${relative}, ${weekday}`, isToday: diffDays === 0 };
  }

  const full = date.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  return { label: full, isToday: false };
}
